import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  FolderUp,
  Play,
  Pause,
  RotateCcw,
  Eye,
  ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';
import ImportAlertSettings from '../components/ImportAlertSettings';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportFournisseurs() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null); // Job detail view
  const [selectedJobFiles, setSelectedJobFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [filesTab, setFilesTab] = useState('all');
  const fileInputRef = useRef(null);

  const intervalRef = useRef(null);

  // Memoize filtered files to avoid recalculation on every render
  const filteredJobFiles = useMemo(() => {
    if (!selectedJobFiles) return [];
    return selectedJobFiles.filter(f => filesTab === 'all' || f.status === filesTab);
  }, [selectedJobFiles, filesTab]);

  // Fetch jobs list (wrapped in ref to avoid dependency issues)
  const fetchJobsRef = useRef(null);
  fetchJobsRef.current = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/import-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const jobsList = res.data.jobs || [];
      setJobs(jobsList);
      
      // Find active job
      const processing = jobsList.find(j => j.status === 'processing');
      if (processing) {
        // Fetch full details for active job
        const detailRes = await axios.get(`${API}/api/import-jobs/${processing.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActiveJob(detailRes.data);
      } else {
        setActiveJob(null);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const fetchJobs = () => fetchJobsRef.current?.();

  // Poll for updates - stable interval that doesn't recreate
  useEffect(() => {
    let isMounted = true;

    const doFetch = async () => {
      if (isMounted && token) {
        await fetchJobsRef.current?.();
      }
    };

    // Initial fetch
    doFetch();
    
    // Poll every 3 seconds
    intervalRef.current = setInterval(doFetch, 3000);
    
    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [token]); // Only depend on token, not on fetchJobs

  // Fetch job details with file list
  const fetchJobDetails = async (jobId) => {
    try {
      const res = await axios.get(`${API}/api/import-jobs/${jobId}?include_files=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedJob(res.data);
      setSelectedJobFiles(res.data.files || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Handle file upload
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) {
      toast.error('Aucun fichier PDF sélectionné');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      for (const file of pdfFiles) {
        formData.append('files', file);
      }
      
      const res = await axios.post(`${API}/api/import-jobs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
        timeout: 600000 // 10 minutes for large uploads
      });
      
      toast.success(`${res.data.total_files} fichiers en cours d'importation`);
      fetchJobs();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle retry
  const handleRetry = async (jobId) => {
    try {
      const res = await axios.post(`${API}/api/import-jobs/${jobId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la relance');
    }
  };

  // Handle cancel
  const handleCancel = async (jobId) => {
    try {
      const res = await axios.post(`${API}/api/import-jobs/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Handle delete
  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`${API}/api/import-jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Import supprimé');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" />En cours</Badge>;
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Terminé</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Échoué</Badge>;
      case 'cancelled':
        return <Badge variant="secondary"><X className="w-3 h-3 mr-1" />Annulé</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const progressPercent = activeJob ? Math.round((activeJob.processed / activeJob.total_files) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderUp className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Import en masse</h1>
            <p className="text-muted-foreground">Importez jusqu'à 2000 factures fournisseurs simultanément</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ImportAlertSettings module="achats" />
          <Button variant="outline" onClick={fetchJobs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Active Job Progress Card */}
      {activeJob && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  Import en cours
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCancel(activeJob.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{activeJob.processed} / {activeJob.total_files} fichiers traités</span>
                  <span className="text-muted-foreground">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-3" />
              </div>
              
              {activeJob.current_file && (
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <FileText className="w-5 h-5 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activeJob.current_file}</p>
                    <p className="text-xs text-muted-foreground">En cours de traitement...</p>
                  </div>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                </div>
              )}
              
              <div className="flex gap-4 text-sm">
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {activeJob.successful} importé{activeJob.successful > 1 ? 's' : ''}
                </span>
                {activeJob.duplicates > 0 && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {activeJob.duplicates} doublon{activeJob.duplicates > 1 ? 's' : ''}
                  </span>
                )}
                {activeJob.failed > 0 && (
                  <span className="text-red-600 flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    {activeJob.failed} échec{activeJob.failed > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Upload Zone */}
      <Card>
        <CardContent className="p-0">
          <div 
            className={`
              border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer
              ${dragOver ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary hover:bg-primary/5'}
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleUpload(e.dataTransfer.files);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              disabled={uploading}
            />
            
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
                <div>
                  <p className="text-xl font-semibold">Upload en cours...</p>
                  <p className="text-muted-foreground">Veuillez patienter</p>
                </div>
                <Progress value={uploadProgress} className="w-64 mx-auto h-2" />
                <p className="text-sm font-medium">{uploadProgress}%</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-semibold">Glissez vos fichiers PDF ici</p>
                  <p className="text-muted-foreground">ou cliquez pour sélectionner</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Jusqu'à <span className="font-semibold text-primary">2000 fichiers</span> simultanément
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Jobs History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historique des imports</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun import récent</p>
              <p className="text-sm">Glissez des fichiers PDF pour commencer</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Fichiers</TableHead>
                  <TableHead>Résultat</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow 
                    key={job.id} 
                    className={`cursor-pointer hover:bg-muted/50 ${selectedJob?.id === job.id ? 'bg-muted' : ''}`}
                    onClick={() => fetchJobDetails(job.id)}
                  >
                    <TableCell className="font-medium">
                      {new Date(job.created_at).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Europe/Paris'
                      })}
                    </TableCell>
                    <TableCell>{job.total_files} fichier{job.total_files > 1 ? 's' : ''}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-green-600">{job.successful} ✓</span>
                        {job.duplicates > 0 && <span className="text-amber-600">{job.duplicates} ⚠</span>}
                        {job.failed > 0 && <span className="text-red-600">{job.failed} ✗</span>}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(job.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => fetchJobDetails(job.id)}
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(job.status === 'completed' || job.status === 'failed') && job.failed > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRetry(job.id)}
                            title="Relancer les échecs"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        {job.status !== 'processing' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(job.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Job Details Panel */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setSelectedJob(null); setSelectedJobFiles(null); }}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Retour
                    </Button>
                    <CardTitle className="text-lg">
                      Détails de l'import du {new Date(selectedJob.created_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </CardTitle>
                  </div>
                  {getStatusBadge(selectedJob.status)}
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold">{selectedJob.total_files}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedJob.successful}</p>
                    <p className="text-sm text-green-600">Importés</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{selectedJob.duplicates}</p>
                    <p className="text-sm text-amber-600">Doublons</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{selectedJob.failed}</p>
                    <p className="text-sm text-red-600">Échecs</p>
                  </div>
                </div>

                {/* Files Tabs */}
                {selectedJobFiles && (
                  <Tabs value={filesTab} onValueChange={setFilesTab}>
                    <TabsList className="mb-4">
                      <TabsTrigger value="all">
                        Tous ({selectedJobFiles.length})
                      </TabsTrigger>
                      <TabsTrigger value="success" className="text-green-600">
                        Importés ({selectedJobFiles.filter(f => f.status === 'success').length})
                      </TabsTrigger>
                      <TabsTrigger value="duplicate" className="text-amber-600">
                        Doublons ({selectedJobFiles.filter(f => f.status === 'duplicate').length})
                      </TabsTrigger>
                      <TabsTrigger value="failed" className="text-red-600">
                        Échecs ({selectedJobFiles.filter(f => f.status === 'failed').length})
                      </TabsTrigger>
                    </TabsList>

                    <div className="max-h-96 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Fichier</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Détail</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredJobFiles.map((file, fileIndex) => (
                              <TableRow key={file.filename}>
                                <TableCell className="text-muted-foreground">{fileIndex + 1}</TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="truncate max-w-xs">{file.filename}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {file.status === 'success' && (
                                    <Badge className="bg-green-500">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Importé
                                    </Badge>
                                  )}
                                  {file.status === 'duplicate' && (
                                    <Badge className="bg-amber-500">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Doublon
                                    </Badge>
                                  )}
                                  {file.status === 'failed' && (
                                    <Badge variant="destructive">
                                      <XCircle className="w-3 h-3 mr-1" />
                                      Échec
                                    </Badge>
                                  )}
                                  {file.status === 'pending' && (
                                    <Badge variant="secondary">
                                      <Clock className="w-3 h-3 mr-1" />
                                      En attente
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {file.status === 'success' && file.invoice_numero && (
                                    <span className="text-green-600">Facture: {file.invoice_numero}</span>
                                  )}
                                  {file.error && (
                                    <span className="text-red-600">{file.error}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Tabs>
                )}

                {/* Actions */}
                {(selectedJob.status === 'completed' || selectedJob.status === 'failed') && selectedJob.failed > 0 && (
                  <div className="mt-4 flex justify-end">
                    <Button onClick={() => handleRetry(selectedJob.id)}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Relancer les {selectedJob.failed} fichier(s) en échec
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
